import {
  AfterViewInit,
  Directive,
  DoCheck,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  Self
} from '@angular/core';
import { NgControl } from '@angular/forms';

import { Subscription } from 'rxjs';
import '../../../../vendor/chosen/chosen.min';

import { CHOSEN_OPTIONS } from './chosen.options.constant';

@Directive({
  selector: 'select[worChosen]'
})
export class ChosenDirective implements AfterViewInit, DoCheck, OnDestroy {
  private _isInvalid    = false;
  private _observer     : MutationObserver = null;
  private _select       : JQuery           = null;
  private _selection    = '';
  private _subscription : Subscription     = null;

  // Read once, when the plugin starts. A clearable select needs a blank first option.
  @Input() isClearable = false;

  constructor (
    private elementRef        : ElementRef<HTMLSelectElement>,
    @Self() private ngControl : NgControl,
    private ngZone            : NgZone
  ) {}

  private _container () : JQuery {
    return this._select.next('.chosen-container');
  }

  // The plugin fires change through jQuery only, which Angular's native listeners never hear.
  private _forwardChange ( event : JQuery.TriggeredEvent ) : void {
    if ( !event.originalEvent ) {
      this.elementRef.nativeElement.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  private _refresh () : void {
    this._selection = this._selectionKey();

    this.ngZone.runOutsideAngular(() => this._select.trigger('chosen:updated'));
  }

  private _selectionKey () : string {
    return Array.from(this.elementRef.nativeElement.selectedOptions, option => option.index).join();
  }

  ngAfterViewInit () : void {
    const select = this.elementRef.nativeElement;

    this.ngZone.runOutsideAngular(() => {
      this._select = $(select)
      .chosen({
        ...CHOSEN_OPTIONS,
        allow_single_deselect: !!this.isClearable && String(this.isClearable) !== 'false'
      })
      .on('change.worChosen', ( event : JQuery.TriggeredEvent ) => this._forwardChange(event));

      // The plugin copies the select's classes; a form state copied once would paint the box red for good.
      this._container().removeClass('ng-dirty ng-invalid ng-pending ng-touched');

      this._observer = new MutationObserver(() => this._refresh());

      this._observer.observe(select, {
        attributeFilter: [ 'data-placeholder', 'disabled', 'label', 'selected', 'value' ],
        characterData  : true,
        childList      : true,
        subtree        : true
      });
    });

    this._selection    = this._selectionKey();
    this._subscription = this.ngControl.valueChanges.subscribe(() => this._refresh());
  }

  ngDoCheck () : void {
    if ( !this._select ) {
      return;
    }

    const isInvalid = !!this.ngControl.dirty && !!this.ngControl.invalid;

    // Writes that emit no value change: emitEvent false, a swapped control, a new option value.
    if ( this._selectionKey() !== this._selection ) {
      this._refresh();
    }

    if ( isInvalid !== this._isInvalid ) {
      this._isInvalid = isInvalid;

      this._container().toggleClass('invalid', isInvalid);
    }
  }

  ngOnDestroy () : void {
    if ( this._observer ) {
      this._observer.disconnect();
    }

    if ( this._subscription ) {
      this._subscription.unsubscribe();
    }

    if ( this._select ) {
      this._select.off('change.worChosen').chosen('destroy');

      // The plugin's destroy leaves its own select handlers behind.
      this._select.off('.chosen');
    }
  }
}
